import React, { useCallback, useEffect, useMemo, useState } from "react"
import { accessFromProps } from "../lib/book-access"
import { BookShell } from "../components/book-shell"
import { PageHeader } from "../components/page-header"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { Building2, Landmark, ArrowUpDown, Settings2 } from "lucide-react"
import { usePageLoading } from "../lib/page-loading"

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      ...(options.headers || {}),
    },
    ...options,
  })
  const contentType = res.headers.get("content-type") || ""
  const data = contentType.includes("application/json") ? await res.json() : null
  if (!res.ok) {
    const message = data?.error ? `${data.error}` : `Request failed (${res.status})`
    throw new Error(message)
  }
  return data
}

function centsToAmount(cents) {
  if (cents === null || cents === undefined) return "₹0.00"
  return `₹${(Number(cents) / 100).toFixed(2)}`
}

function TextInput({ label, value, onChange, type = "text", placeholder }) {
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-ring/20" />
    </label>
  )
}

function SelectInput({ label, value, onChange, options }) {
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-ring/20">
        <option value="">Select…</option>
        {options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
    </label>
  )
}

function AccountsPanel() {
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  usePageLoading(loading)
  const [error, setError] = useState("")
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState({ account_name: "", bank_name: "", account_type: "checking", opening_balance_cents: "", notes: "" })

  const load = useCallback(async () => {
    try { setLoading(true); setError(""); const d = await fetchJson("/api/banking/accounts"); setAccounts(d || []) }
    catch (e) { setError(e.message) } finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  async function onSubmit(e) {
    e.preventDefault()
    try {
      setError("")
      const body = JSON.stringify({ bank_account: { ...form, opening_balance_cents: Math.round(Number(form.opening_balance_cents || "0") * 100) } })
      if (editingId) { await fetchJson(`/api/banking/accounts/${editingId}`, { method: "PUT", body }) }
      else { await fetchJson("/api/banking/accounts", { method: "POST", body }) }
      await load(); setEditingId(null); setForm({ account_name: "", bank_name: "", account_type: "checking", opening_balance_cents: "", notes: "" })
    } catch (e2) { setError(e2.message) }
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader><CardTitle>{editingId ? "Edit account" : "Add bank account"}</CardTitle></CardHeader>
        <CardContent>
          {error ? <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
          <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
            <TextInput label="Account Name" value={form.account_name} onChange={(v) => setForm((f) => ({ ...f, account_name: v }))} />
            <TextInput label="Bank Name" value={form.bank_name} onChange={(v) => setForm((f) => ({ ...f, bank_name: v }))} />
            <SelectInput label="Type" value={form.account_type} onChange={(v) => setForm((f) => ({ ...f, account_type: v }))}
              options={[{ value: "checking", label: "Checking" }, { value: "savings", label: "Savings" }, { value: "credit_card", label: "Credit Card" }]} />
            <TextInput label="Opening Balance (₹)" value={form.opening_balance_cents} onChange={(v) => setForm((f) => ({ ...f, opening_balance_cents: v }))} type="number" />
            <div className="md:col-span-2"><TextInput label="Notes" value={form.notes} onChange={(v) => setForm((f) => ({ ...f, notes: v }))} /></div>
            <div className="md:col-span-2 flex gap-2">
              <Button type="submit">{editingId ? "Save" : "Create"}</Button>
              {editingId ? <Button type="button" variant="ghost" onClick={() => { setEditingId(null); setForm({ account_name: "", bank_name: "", account_type: "checking", opening_balance_cents: "", notes: "" }) }}>Cancel</Button> : null}
            </div>
          </form>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Bank Accounts</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead><tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Bank</th><th className="py-2 pr-4">Type</th><th className="py-2 pr-4 text-right">Balance</th><th className="py-2">Actions</th>
                </tr></thead>
                <tbody>
                  {accounts.map((a) => (
                    <tr key={a.id} className="border-b border-border/60">
                      <td className="py-2 pr-4 font-medium">{a.account_name}</td>
                      <td className="py-2 pr-4 text-muted-foreground">{a.bank_name || "—"}</td>
                      <td className="py-2 pr-4 capitalize text-muted-foreground">{a.account_type}</td>
                      <td className="py-2 pr-4 text-right">{centsToAmount(a.current_balance_cents)}</td>
                      <td className="py-2"><Button type="button" variant="ghost" className="h-8 px-2" onClick={() => { setEditingId(a.id); setForm({ account_name: a.account_name, bank_name: a.bank_name || "", account_type: a.account_type, opening_balance_cents: String((a.opening_balance_cents || 0) / 100), notes: a.notes || "" }) }}>Edit</Button></td>
                    </tr>
                  ))}
                  {accounts.length === 0 ? <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">No accounts yet.</td></tr> : null}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function TransactionsPanel() {
  const [txns, setTxns] = useState([])
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  usePageLoading(loading)
  const [error, setError] = useState("")
  const [accountFilter, setAccountFilter] = useState("")

  const load = useCallback(async () => {
    try { setLoading(true); setError(""); const [t, a] = await Promise.all([fetchJson("/api/banking/transactions"), fetchJson("/api/banking/accounts")]); setTxns(t || []); setAccounts(a || []) }
    catch (e) { setError(e.message) } finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle>Transactions</CardTitle>
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground">Account</label>
          <select value={accountFilter} onChange={(e) => setAccountFilter(e.target.value)} className="rounded-md border border-border bg-white px-2 py-1 text-sm">
            <option value="">All</option>
            {accounts.map((a) => (<option key={a.id} value={a.id}>{a.account_name}</option>))}
          </select>
        </div>
      </CardHeader>
      <CardContent>
        {error ? <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
        {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead><tr className="border-b border-border text-left text-muted-foreground">
                <th className="py-2 pr-4">Date</th><th className="py-2 pr-4">Description</th><th className="py-2 pr-4">Account</th><th className="py-2 pr-4 text-right">Amount</th><th className="py-2 pr-4">Type</th><th className="py-2">Status</th>
              </tr></thead>
              <tbody>
                {txns.filter((t) => !accountFilter || String(t.bank_account_id) === accountFilter).map((t) => (
                  <tr key={t.id} className="border-b border-border/60">
                    <td className="py-2 pr-4 whitespace-nowrap">{t.transaction_date}</td>
                    <td className="py-2 pr-4 max-w-[200px] truncate">{t.description}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{t.bank_account?.account_name || "—"}</td>
                    <td className="py-2 pr-4 text-right">{centsToAmount(t.amount_cents)}</td>
                    <td className="py-2 pr-4 capitalize">{t.transaction_type}</td>
                    <td className="py-2 capitalize">{t.status}</td>
                  </tr>
                ))}
                {txns.length === 0 ? <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">No transactions. Import a CSV or add manually.</td></tr> : null}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function ReconciliationPanel() {
  const [accounts, setAccounts] = useState([])
  const [selectedAccount, setSelectedAccount] = useState("")
  const [txns, setTxns] = useState([])
  const [loading, setLoading] = useState(false)
  usePageLoading(loading)
  const [error, setError] = useState("")
  const [selected, setSelected] = useState(new Set())

  useEffect(() => { fetchJson("/api/banking/accounts").then(setAccounts).catch(() => {}) }, [])

  async function loadUnreconciled() {
    if (!selectedAccount) return
    try { setLoading(true); setError(""); const d = await fetchJson(`/api/banking/transactions?bank_account_id=${selectedAccount}&status=unreconciled`); setTxns(d || []) }
    catch (e) { setError(e.message) } finally { setLoading(false) }
  }

  async function reconcileSelected() {
    try { setError(""); await fetchJson(`/api/banking/accounts/${selectedAccount}/reconcile`, { method: "POST", body: JSON.stringify({ transaction_ids: Array.from(selected) }) }); setSelected(new Set()); await loadUnreconciled() }
    catch (e) { setError(e.message) }
  }

  function toggle(id) { setSelected((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next }) }

  return (
    <Card>
      <CardHeader><CardTitle>Reconciliation</CardTitle></CardHeader>
      <CardContent className="grid gap-4">
        {error ? <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
        <div className="flex flex-wrap items-end gap-2">
          <SelectInput label="Account" value={selectedAccount} onChange={setSelectedAccount} options={accounts.map((a) => ({ value: String(a.id), label: a.account_name }))} />
          <Button onClick={loadUnreconciled} disabled={!selectedAccount || loading}>Load</Button>
          {selected.size > 0 ? <Button onClick={reconcileSelected}>Reconcile ({selected.size})</Button> : null}
        </div>
        {txns.length > 0 ? (
          <div className="overflow-x-auto rounded-xl border border-border/90 bg-card shadow-soft">
            <table className="min-w-full border-collapse text-xs">
              <thead className="border-b border-border bg-muted/35 text-left text-muted-foreground">
                <tr><th className="px-2 py-1.5 w-8"></th><th className="px-2 py-1.5 font-medium">Date</th><th className="px-2 py-1.5 font-medium">Description</th><th className="px-2 py-1.5 text-right font-medium">Amount</th></tr>
              </thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={t.id} className="border-b border-border/60">
                    <td className="px-2 py-1"><input type="checkbox" checked={selected.has(t.id)} onChange={() => toggle(t.id)} /></td>
                    <td className="px-2 py-1">{t.transaction_date}</td>
                    <td className="px-2 py-1">{t.description}</td>
                    <td className="px-2 py-1 text-right">{centsToAmount(t.amount_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : selectedAccount && !loading ? <p className="text-xs text-muted-foreground">No unreconciled transactions.</p> : null}
      </CardContent>
    </Card>
  )
}

export function BankingPage(props) {
  const {
    active_slug,
    section,
    section_title,
    user_name,
    access_role,
    can_create_company,
    can_enter_transactions,
    can_approve_documents,
    can_manage_company_settings,
    is_viewer,
  } = props

  const access = accessFromProps(props)
  const key = section || "accounts"

  return (
    <BookShell activeSlug={active_slug || "banking"} userName={user_name} access={access}>
      <div className="flex flex-col gap-6">
        <PageHeader title={section_title} context="Banking" />

        {key === "accounts" ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Landmark className="h-5 w-5 shrink-0" />
            <span className="text-sm">Manage bank and credit card accounts</span>
          </div>
        ) : null}
        {key === "accounts" ? <AccountsPanel /> : null}

        {key === "transactions" ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <ArrowUpDown className="h-5 w-5 shrink-0" />
            <span className="text-sm">View and manage transactions</span>
          </div>
        ) : null}
        {key === "transactions" ? <TransactionsPanel /> : null}

        {key === "reconciliation" ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Building2 className="h-5 w-5 shrink-0" />
            <span className="text-sm">Match transactions to reconcile accounts</span>
          </div>
        ) : null}
        {key === "reconciliation" ? <ReconciliationPanel /> : null}

        {key === "rules" ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Settings2 className="h-5 w-5 shrink-0" />
            <span className="text-sm">Auto-categorization rules</span>
          </div>
        ) : null}
        {key === "rules" ? <p className="text-sm text-muted-foreground">Use the API to manage bank rules.</p> : null}
      </div>
    </BookShell>
  )
}
